// ============================================================================
// MODAL CADASTRO ALUNO — DASHBOARD ADMIN
// ============================================================================
// Arquivo: src/components/Dashboard_ADM/ModalCadastroAluno.tsx
// CREATED: 2026-04-09
// ✅ Seletor de filiado com busca por nome ou ID
// ✅ Upload de foto de perfil (S3 via /documents/public-upload)
// ✅ Upload de documentos (CPF/RG e comprovante)
// ✅ Mesmo formulário do CadastroAlunoForm do portal
// ============================================================================

import React, { useState, useRef, useCallback, useEffect } from 'react';
import {
  X, ChevronRight, ChevronLeft, Search, Upload, CheckCircle,
  Loader2, AlertCircle, User, Mail, Phone, CreditCard, Calendar,
  MapPin, BookOpen, Clock, Building2, Camera, FileText,
  Trash2, UserPlus,
} from 'lucide-react';
import apiClient from '../../services/api';
import { API_BASE } from '../../utils/apiConfig';

// ─── Types ───────────────────────────────────────────────────────────────────
interface Filiado {
  id: string;
  systemId: string;
  schoolName: string;
  city?: string;
  state?: string;
  logoUrl?: string;
}

interface DocSlot {
  arquivo:  File | null;
  preview:  string | null;
  enviando: boolean;
  enviado:  boolean;
  erro:     string | null;
}

interface Props {
  onFechar:  () => void;
  onSucesso: () => void;
}

// ─── Helpers ─────────────────────────────────────────────────────────────────
const UFS = ['AC','AL','AP','AM','BA','CE','DF','ES','GO','MA','MT','MS',
             'MG','PA','PB','PR','PE','PI','RJ','RN','RS','RO','RR','SC','SP','SE','TO'];

const slotVazio = (): DocSlot => ({
  arquivo: null, preview: null, enviando: false, enviado: false, erro: null,
});

const inputCls = (err = false) =>
  `w-full px-4 py-3 rounded-xl border ${err ? 'border-red-400 bg-red-50' : 'border-gray-300 bg-white'} ` +
  `focus:outline-none focus:ring-2 focus:ring-blue-500 text-gray-900 text-sm font-medium transition-all`;

const getErr = (e: any, fb = 'Erro'): string => {
  const d = e?.response?.data;
  if (Array.isArray(d?.message))      return d.message.join(', ');
  if (typeof d?.message === 'string') return d.message;
  if (typeof e?.message === 'string') return e.message;
  return fb;
};

// ─── Sub-componente: linha de campo ──────────────────────────────────────────
const Field: React.FC<{ label: string; required?: boolean; error?: string; children: React.ReactNode }> = ({
  label, required, error, children,
}) => (
  <div>
    <label className="block text-xs font-bold text-gray-500 uppercase tracking-wide mb-1.5">
      {label}{required && <span className="text-red-500 ml-1">*</span>}
    </label>
    {children}
    {error && (
      <p className="mt-1 text-xs text-red-500 flex items-center gap-1">
        <AlertCircle className="w-3.5 h-3.5 flex-shrink-0" />{error}
      </p>
    )}
  </div>
);

// ─── Sub-componente: card de documento ───────────────────────────────────────
const DocCard: React.FC<{
  titulo: string;
  descricao: string;
  obrigatorio?: boolean;
  slot: DocSlot;
  accept?: string;
  onSelecionar: (f: File) => void;
  onRemover: () => void;
}> = ({ titulo, descricao, obrigatorio, slot, accept = 'image/*,.pdf', onSelecionar, onRemover }) => (
  <div className={`rounded-xl border-2 overflow-hidden transition-all ${
    slot.enviado   ? 'border-green-400 bg-green-50'
    : slot.arquivo ? 'border-blue-300 bg-blue-50/30'
    : 'border-dashed border-gray-300 bg-white hover:border-blue-400'}`}>
    <div className="flex items-center gap-2.5 px-4 py-2.5 border-b border-gray-100 bg-white/70">
      <FileText className="w-4 h-4 text-gray-400" />
      <div className="flex-1">
        <p className="text-xs font-bold text-gray-700 uppercase tracking-wide leading-none">{titulo}</p>
        <p className="text-[10px] text-gray-400 mt-0.5">{descricao}</p>
      </div>
      {obrigatorio && (
        <span className="text-[9px] font-bold px-2 py-0.5 rounded-full bg-red-100 text-red-600 border border-red-200">
          Obrigatório
        </span>
      )}
    </div>

    {slot.enviado ? (
      <div className="flex items-center gap-3 px-4 py-3">
        {slot.preview
          ? <img src={slot.preview} alt="" className="w-12 h-12 rounded-lg object-cover border border-green-300" />
          : <div className="w-12 h-12 rounded-lg bg-green-100 flex items-center justify-center"><FileText className="w-5 h-5 text-green-600" /></div>}
        <div className="flex-1 min-w-0">
          <p className="text-xs font-semibold text-green-700 truncate">{slot.arquivo?.name}</p>
          <p className="text-[10px] text-green-500 mt-0.5 flex items-center gap-1"><CheckCircle className="w-3 h-3" /> Enviado</p>
        </div>
      </div>
    ) : slot.arquivo ? (
      <div className="px-4 py-3">
        {slot.preview && <img src={slot.preview} alt="" className="w-full max-h-28 object-contain rounded-lg mb-2 border border-gray-200" />}
        <div className="flex items-center gap-2">
          <span className="text-xs text-gray-700 truncate flex-1">{slot.arquivo.name}</span>
          <span className="text-[10px] text-gray-400">{(slot.arquivo.size / 1024).toFixed(0)} KB</span>
          <button type="button" onClick={onRemover}
            className="p-1 rounded-lg bg-red-100 text-red-500 hover:bg-red-200 transition-colors">
            <Trash2 className="w-3.5 h-3.5" />
          </button>
        </div>
        {slot.erro && <p className="text-xs text-red-500 mt-1 flex items-center gap-1"><AlertCircle className="w-3.5 h-3.5" />{slot.erro}</p>}
      </div>
    ) : (
      <label className="flex flex-col items-center justify-center py-5 cursor-pointer">
        <div className="w-9 h-9 rounded-xl bg-gray-100 flex items-center justify-center mb-2">
          <Upload className="w-4 h-4 text-gray-400" />
        </div>
        <p className="text-xs text-gray-500 font-medium">Clique para selecionar</p>
        <p className="text-[10px] text-gray-400 mt-0.5">JPG, PNG, PDF — máx. 5 MB</p>
        <input type="file" accept={accept} className="hidden"
          onChange={e => { const f = e.target.files?.[0]; if (f) onSelecionar(f); }} />
      </label>
    )}
  </div>
);

// ─── Componente principal ─────────────────────────────────────────────────────
const ModalCadastroAluno: React.FC<Props> = ({ onFechar, onSucesso }) => {
  const [etapa, setEtapa] = useState<1 | 2 | 3 | 4>(1);
  const [salvando, setSalvando] = useState(false);
  const [erro, setErro] = useState<string | null>(null);
  const [sucesso, setSucesso] = useState<string | null>(null);

  // ── Etapa 1: Seletor de filiado ──────────────────────────────────────────
  const [filiadoBusca, setFiliadoBusca]     = useState('');
  const [filiadoInput, setFiliadoInput]     = useState('');
  const [filiados, setFiliados]             = useState<Filiado[]>([]);
  const [loadingFiliados, setLoadingFiliados] = useState(false);
  const [filiadoSelecionado, setFiliadoSelecionado] = useState<Filiado | null>(null);

  // ── Etapa 2: Dados do aluno ──────────────────────────────────────────────
  const [form, setForm] = useState({
    fullName: '', email: '', cpf: '', cin: '', phone: '', whatsapp: '',
    birthdate: '', address: '', city: '', state: '', postalCode: '',
    courseName: '', courseWorkload: '', courseStartDate: '', courseEndDate: '',
  });
  const [formErros, setFormErros] = useState<Record<string, string>>({});

  // ── Etapa 3: Foto de perfil ──────────────────────────────────────────────
  const [fotoFile, setFotoFile]     = useState<File | null>(null);
  const [fotoPreview, setFotoPreview] = useState<string | null>(null);
  const fotoRef = useRef<HTMLInputElement>(null);

  // ── Etapa 4: Documentos ──────────────────────────────────────────────────
  const [slotDoc1, setSlotDoc1] = useState<DocSlot>(slotVazio()); // CPF/RG
  const [slotDoc2, setSlotDoc2] = useState<DocSlot>(slotVazio()); // Comprovante
  const [studentId, setStudentId] = useState<string | null>(null);
  const [systemId,  setSystemId]  = useState<string | null>(null);

  // ── Busca de filiados ────────────────────────────────────────────────────
  useEffect(() => {
    const t = setTimeout(() => setFiliadoBusca(filiadoInput), 350);
    return () => clearTimeout(t);
  }, [filiadoInput]);

  const buscarFiliados = useCallback(async () => {
    if (filiadoBusca.trim().length < 2) { setFiliados([]); return; }
    setLoadingFiliados(true);
    try {
      const data: any = await apiClient.get(
        `/affiliates?page=1&limit=20&search=${encodeURIComponent(filiadoBusca)}`
      );
      setFiliados((data.affiliates ?? data.results ?? []).map((a: any) => ({
        id:         a.id,
        systemId:   a.systemId ?? '',
        schoolName: a.schoolName ?? a.nomeSolicitante ?? '',
        city:       a.city  ?? a.cidade ?? '',
        state:      a.state ?? a.estado ?? '',
        logoUrl:    a.logoUrl ?? undefined,
      })));
    } catch { setFiliados([]); }
    finally { setLoadingFiliados(false); }
  }, [filiadoBusca]);

  useEffect(() => { buscarFiliados(); }, [buscarFiliados]);

  // ── Handlers form ────────────────────────────────────────────────────────
  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setForm(p => ({ ...p, [name]: value }));
    if (formErros[name]) setFormErros(p => ({ ...p, [name]: '' }));
  };

  const handleFoto = (e: React.ChangeEvent<HTMLInputElement>) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (!f.type.startsWith('image/')) { setErro('Selecione apenas imagens'); return; }
    if (f.size > 5 * 1024 * 1024)    { setErro('Máximo 5MB'); return; }
    setFotoFile(f);
    const r = new FileReader();
    r.onload = ev => setFotoPreview(ev.target?.result as string);
    r.readAsDataURL(f);
    setErro(null);
  };

  const selecionarDoc = (set: React.Dispatch<React.SetStateAction<DocSlot>>, file: File) => {
    const preview = file.type.startsWith('image/') ? URL.createObjectURL(file) : null;
    set({ arquivo: file, preview, enviando: false, enviado: false, erro: null });
  };

  const removerDoc = (set: React.Dispatch<React.SetStateAction<DocSlot>>, slot: DocSlot) => {
    if (slot.preview) URL.revokeObjectURL(slot.preview);
    set(slotVazio());
  };

  // ── Validar etapa 2 ──────────────────────────────────────────────────────
  const validarDados = () => {
    const e: Record<string, string> = {};
    if (!form.fullName.trim())    e.fullName       = 'Obrigatório';
    if (!form.email.includes('@')) e.email         = 'E-mail inválido';
    if (!form.cpf.trim() && !form.cin.trim()) e.cpf = 'Informe CPF ou CIN';
    if (!form.birthdate)          e.birthdate      = 'Obrigatório';
    if (!form.phone.trim())       e.phone          = 'Obrigatório';
    if (!form.courseName.trim())  e.courseName     = 'Obrigatório';
    if (!form.courseWorkload)     e.courseWorkload  = 'Obrigatório';
    if (!form.courseStartDate)    e.courseStartDate = 'Obrigatório';
    if (!form.courseEndDate)      e.courseEndDate   = 'Obrigatório';
    setFormErros(e);
    return Object.keys(e).length === 0;
  };

  // ── Cadastrar aluno (etapa 2 → 3) ────────────────────────────────────────
  const handleCadastrar = async () => {
    if (!filiadoSelecionado) { setErro('Selecione uma escola/filiado'); return; }
    if (!validarDados())     return;
    setSalvando(true); setErro(null);
    try {
      const toISO = (d: string) => d ? new Date(d).toISOString() : undefined;
      const payload = {
        affiliateId:     filiadoSelecionado.id,
        fullName:        form.fullName.trim(),
        email:           form.email.trim(),
        cpf:             form.cpf.trim()        || undefined,
        cin:             form.cin.trim()        || undefined,
        phone:           form.phone.trim()      || undefined,
        whatsapp:        form.whatsapp.trim()   || undefined,
        birthdate:       toISO(form.birthdate),
        address:         form.address.trim()    || undefined,
        city:            form.city.trim()       || undefined,
        state:           form.state             || undefined,
        postalCode:      form.postalCode.trim() || undefined,
        courseName:      form.courseName.trim() || undefined,
        courseWorkload:  form.courseWorkload ? Number(form.courseWorkload) : undefined,
        courseStartDate: toISO(form.courseStartDate),
        courseEndDate:   toISO(form.courseEndDate),
      };
      const data: any = await apiClient.post('/students/enroll', payload);
      setStudentId(data.id ?? data.student?.id);
      setSystemId(data.systemId ?? data.student?.systemId);
      setEtapa(3);
    } catch (e: any) {
      setErro(getErr(e, 'Erro ao cadastrar aluno'));
    } finally { setSalvando(false); }
  };

  // ── Upload foto (etapa 3 → 4) ────────────────────────────────────────────
  const handleProximoFoto = async () => {
    if (!fotoFile || !filiadoSelecionado || !studentId) { setEtapa(4); return; }
    setSalvando(true); setErro(null);
    try {
      const fd = new FormData();
      fd.append('file', fotoFile);
      const url = `${API_BASE}/documents/public-upload` +
        `?affiliateId=${encodeURIComponent(filiadoSelecionado.id)}` +
        `&documentType=foto&description=Foto%20do%20aluno`;
      const token = localStorage.getItem('auth_token') ?? localStorage.getItem('token') ?? '';
      const res = await fetch(url, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
        body: fd,
      });
      if (res.ok) {
        const d = await res.json();
        const photoUrl = d.url ?? d.fileUrl ?? d.path ?? null;
        if (photoUrl && studentId) {
          await apiClient.patch(`/students/${studentId}`, { photoUrl }).catch(() => {});
        }
      }
    } catch { /* silencioso — foto é opcional */ }
    finally { setSalvando(false); }
    setEtapa(4);
  };

  // ── Upload documentos (etapa 4 → concluído) ──────────────────────────────
  const uploadDoc = async (
    slot: DocSlot,
    setSlot: React.Dispatch<React.SetStateAction<DocSlot>>,
    docType: string,
    description: string,
  ) => {
    if (!slot.arquivo || !filiadoSelecionado) return;
    setSlot(p => ({ ...p, enviando: true, erro: null }));
    try {
      const fd = new FormData();
      fd.append('file', slot.arquivo);
      const url = `${API_BASE}/documents/public-upload` +
        `?affiliateId=${encodeURIComponent(filiadoSelecionado.id)}` +
        `&documentType=${docType}&description=${encodeURIComponent(description)}`;
      const token = localStorage.getItem('auth_token') ?? localStorage.getItem('token') ?? '';
      const res = await fetch(url, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
        body: fd,
      });
      if (!res.ok) throw new Error(`Erro ${res.status}`);
      setSlot(p => ({ ...p, enviando: false, enviado: true }));
    } catch (e: any) {
      setSlot(p => ({ ...p, enviando: false, erro: e.message ?? 'Erro no upload' }));
    }
  };

  const handleFinalizar = async () => {
    setSalvando(true);
    if (slotDoc1.arquivo && !slotDoc1.enviado)
      await uploadDoc(slotDoc1, setSlotDoc1, 'rg',          'Documento de Identidade');
    if (slotDoc2.arquivo && !slotDoc2.enviado)
      await uploadDoc(slotDoc2, setSlotDoc2, 'comprovante', 'Comprovante de Endereço');
    setSalvando(false);
    setSucesso(systemId ?? 'Aluno cadastrado com sucesso!');
  };

  // ── Render ───────────────────────────────────────────────────────────────
  const ETAPAS = ['Escola', 'Dados', 'Foto', 'Documentos'];

  return (
    <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4"
      onClick={e => e.target === e.currentTarget && onFechar()}>
      <div className="bg-white rounded-2xl w-full max-w-2xl max-h-[94vh] overflow-hidden flex flex-col shadow-2xl">

        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200 bg-gray-50 flex-shrink-0">
          <div>
            <h2 className="text-xl font-black text-gray-900">Novo Aluno</h2>
            <p className="text-sm text-gray-500">Etapa {etapa} de 4 — {ETAPAS[etapa - 1]}</p>
          </div>
          {/* Steps */}
          <div className="hidden md:flex items-center gap-2">
            {ETAPAS.map((label, i) => {
              const n = i + 1;
              const done   = etapa > n;
              const active = etapa === n;
              return (
                <React.Fragment key={n}>
                  {i > 0 && <div className="w-5 h-px bg-gray-300" />}
                  <div className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-black border-2 transition-colors
                    ${done   ? 'bg-green-500 border-green-500 text-white'
                    : active ? 'bg-blue-600 border-blue-600 text-white'
                    : 'bg-white border-gray-300 text-gray-400'}`}>
                    {done ? '✓' : n}
                  </div>
                </React.Fragment>
              );
            })}
          </div>
          <button onClick={onFechar} className="p-2 hover:bg-gray-200 rounded-lg text-gray-500 transition-colors">
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto p-6">

          {/* Erro global */}
          {erro && (
            <div className="flex items-center gap-3 p-4 bg-red-50 border border-red-200 rounded-xl text-red-700 mb-4">
              <AlertCircle className="w-5 h-5 flex-shrink-0" />
              <p className="text-sm font-semibold">{erro}</p>
            </div>
          )}

          {/* ── ETAPA 1: Selecionar Filiado ──────────────────────────────── */}
          {etapa === 1 && (
            <div className="space-y-4">
              <p className="text-sm text-gray-600">Pesquise e selecione a escola ou filiado ao qual este aluno pertence.</p>

              <div className="relative">
                <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                <input value={filiadoInput} onChange={e => setFiliadoInput(e.target.value)}
                  placeholder="Buscar por nome da escola ou ID (FIL-...)"
                  className="w-full pl-12 pr-4 py-3 border-2 border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent text-gray-900" />
              </div>

              {/* Filiado já selecionado */}
              {filiadoSelecionado && (
                <div className="flex items-center gap-3 p-4 bg-green-50 border-2 border-green-400 rounded-xl">
                  {filiadoSelecionado.logoUrl
                    ? <img src={filiadoSelecionado.logoUrl} alt="" className="w-12 h-12 rounded-lg object-cover border border-green-300" />
                    : <div className="w-12 h-12 rounded-lg bg-green-100 flex items-center justify-center"><Building2 className="w-6 h-6 text-green-600" /></div>}
                  <div className="flex-1 min-w-0">
                    <p className="font-black text-gray-900">{filiadoSelecionado.schoolName}</p>
                    <p className="text-xs font-mono text-green-600">{filiadoSelecionado.systemId}</p>
                    {(filiadoSelecionado.city || filiadoSelecionado.state) && (
                      <p className="text-xs text-gray-500">{[filiadoSelecionado.city, filiadoSelecionado.state].filter(Boolean).join(', ')}</p>
                    )}
                  </div>
                  <CheckCircle className="w-6 h-6 text-green-500 flex-shrink-0" />
                  <button type="button" onClick={() => setFiliadoSelecionado(null)}
                    className="p-1.5 rounded-lg bg-red-100 text-red-500 hover:bg-red-200 transition-colors">
                    <X className="w-4 h-4" />
                  </button>
                </div>
              )}

              {/* Resultados da busca */}
              {loadingFiliados && (
                <div className="flex items-center justify-center py-6">
                  <Loader2 className="w-6 h-6 text-blue-500 animate-spin" />
                </div>
              )}

              {!loadingFiliados && filiados.length > 0 && !filiadoSelecionado && (
                <div className="border border-gray-200 rounded-xl overflow-hidden divide-y divide-gray-100">
                  {filiados.map(f => (
                    <button key={f.id} type="button" onClick={() => { setFiliadoSelecionado(f); setFiliados([]); setFiliadoInput(''); }}
                      className="w-full flex items-center gap-3 px-4 py-3 hover:bg-blue-50 transition-colors text-left">
                      {f.logoUrl
                        ? <img src={f.logoUrl} alt="" className="w-10 h-10 rounded-lg object-cover border border-gray-200 flex-shrink-0" />
                        : <div className="w-10 h-10 rounded-lg bg-blue-100 flex items-center justify-center flex-shrink-0"><Building2 className="w-5 h-5 text-blue-600" /></div>}
                      <div className="flex-1 min-w-0">
                        <p className="font-bold text-gray-900 truncate">{f.schoolName}</p>
                        <p className="text-xs text-gray-500">{f.systemId} {f.city && `· ${[f.city, f.state].filter(Boolean).join(', ')}`}</p>
                      </div>
                    </button>
                  ))}
                </div>
              )}

              {!loadingFiliados && filiadoInput.trim().length >= 2 && filiados.length === 0 && !filiadoSelecionado && (
                <div className="text-center py-6 text-gray-400">
                  <Building2 className="w-10 h-10 mx-auto mb-2 opacity-30" />
                  <p className="text-sm">Nenhuma escola encontrada para "{filiadoInput}"</p>
                </div>
              )}

              {filiadoInput.trim().length < 2 && !filiadoSelecionado && (
                <p className="text-xs text-gray-400 text-center py-4">Digite ao menos 2 caracteres para buscar</p>
              )}
            </div>
          )}

          {/* ── ETAPA 2: Dados do Aluno ───────────────────────────────────── */}
          {etapa === 2 && (
            <div className="space-y-5">
              {/* Escola selecionada */}
              <div className="flex items-center gap-3 p-3 bg-blue-50 border border-blue-200 rounded-xl">
                <Building2 className="w-4 h-4 text-blue-600 flex-shrink-0" />
                <div className="min-w-0">
                  <p className="text-xs text-blue-500 font-semibold">Escola vinculada</p>
                  <p className="text-sm font-black text-blue-900 truncate">{filiadoSelecionado?.schoolName}</p>
                </div>
              </div>

              {/* Dados pessoais */}
              <div className="space-y-3">
                <p className="text-xs font-black text-gray-400 uppercase tracking-widest flex items-center gap-2"><User className="w-3.5 h-3.5" /> Dados Pessoais</p>
                <Field label="Nome Completo" required error={formErros.fullName}>
                  <input name="fullName" value={form.fullName} onChange={handleChange}
                    className={inputCls(!!formErros.fullName)} placeholder="Nome do aluno" />
                </Field>
                <div className="grid grid-cols-2 gap-3">
                  <Field label="E-mail" required error={formErros.email}>
                    <input name="email" type="email" value={form.email} onChange={handleChange}
                      className={inputCls(!!formErros.email)} placeholder="email@dominio.com" />
                  </Field>
                  <Field label="Telefone" required error={formErros.phone}>
                    <input name="phone" value={form.phone} onChange={handleChange}
                      className={inputCls(!!formErros.phone)} placeholder="(00) 00000-0000" />
                  </Field>
                  <Field label="WhatsApp">
                    <input name="whatsapp" value={form.whatsapp} onChange={handleChange}
                      className={inputCls()} placeholder="(00) 00000-0000" />
                  </Field>
                  <Field label="Data de Nascimento" required error={formErros.birthdate}>
                    <input name="birthdate" type="date" value={form.birthdate} onChange={handleChange}
                      className={inputCls(!!formErros.birthdate)} />
                  </Field>
                  <Field label="CPF" error={formErros.cpf}>
                    <input name="cpf" value={form.cpf} onChange={handleChange}
                      className={inputCls(!!formErros.cpf)} placeholder="000.000.000-00" maxLength={14} />
                  </Field>
                  <Field label="CIN (alternativa ao CPF)">
                    <input name="cin" value={form.cin} onChange={handleChange}
                      className={inputCls()} placeholder="Número do CIN" />
                  </Field>
                </div>
                <p className="text-[11px] text-gray-400">Informe CPF <em>ou</em> CIN — ao menos um é obrigatório.</p>
              </div>

              {/* Endereço */}
              <div className="space-y-3">
                <p className="text-xs font-black text-gray-400 uppercase tracking-widest flex items-center gap-2"><MapPin className="w-3.5 h-3.5" /> Endereço</p>
                <div className="grid grid-cols-2 gap-3">
                  <Field label="CEP">
                    <input name="postalCode" value={form.postalCode} onChange={handleChange}
                      className={inputCls()} placeholder="00000-000" maxLength={9} />
                  </Field>
                  <div className="col-span-2">
                    <Field label="Endereço">
                      <input name="address" value={form.address} onChange={handleChange}
                        className={inputCls()} placeholder="Rua, número, complemento" />
                    </Field>
                  </div>
                  <Field label="Cidade">
                    <input name="city" value={form.city} onChange={handleChange}
                      className={inputCls()} placeholder="Cidade" />
                  </Field>
                  <Field label="Estado">
                    <select name="state" value={form.state} onChange={handleChange} className={inputCls()}>
                      <option value="">Selecione</option>
                      {UFS.map(uf => <option key={uf} value={uf}>{uf}</option>)}
                    </select>
                  </Field>
                </div>
              </div>

              {/* Curso */}
              <div className="space-y-3">
                <p className="text-xs font-black text-gray-400 uppercase tracking-widest flex items-center gap-2"><BookOpen className="w-3.5 h-3.5" /> Curso</p>
                <Field label="Nome do Curso" required error={formErros.courseName}>
                  <input name="courseName" value={form.courseName} onChange={handleChange}
                    className={inputCls(!!formErros.courseName)} placeholder="Ex: Cosmetologia Avançada" />
                </Field>
                <div className="grid grid-cols-3 gap-3">
                  <Field label="Carga Horária (h)" required error={formErros.courseWorkload}>
                    <input name="courseWorkload" type="number" min={1} value={form.courseWorkload} onChange={handleChange}
                      className={inputCls(!!formErros.courseWorkload)} placeholder="120" />
                  </Field>
                  <Field label="Data Início" required error={formErros.courseStartDate}>
                    <input name="courseStartDate" type="date" value={form.courseStartDate} onChange={handleChange}
                      className={inputCls(!!formErros.courseStartDate)} />
                  </Field>
                  <Field label="Data Término" required error={formErros.courseEndDate}>
                    <input name="courseEndDate" type="date" value={form.courseEndDate} onChange={handleChange}
                      className={inputCls(!!formErros.courseEndDate)} />
                  </Field>
                </div>
              </div>
            </div>
          )}

          {/* ── ETAPA 3: Foto de Perfil ───────────────────────────────────── */}
          {etapa === 3 && (
            <div className="space-y-5">
              {/* ID gerado */}
              {systemId && (
                <div className="p-4 bg-green-50 border border-green-200 rounded-xl flex items-center gap-3">
                  <CheckCircle className="w-5 h-5 text-green-600 flex-shrink-0" />
                  <div>
                    <p className="text-sm text-green-700 font-semibold">Aluno cadastrado!</p>
                    <p className="text-lg font-black text-green-800 font-mono">{systemId}</p>
                  </div>
                </div>
              )}

              <p className="text-sm text-gray-600">Adicione uma foto de perfil para o aluno. Ela aparecerá na carteirinha digital. <span className="text-gray-400">(opcional)</span></p>

              {fotoPreview ? (
                <div className="flex items-center gap-4 p-4 border-2 border-blue-300 bg-blue-50/30 rounded-xl">
                  <img src={fotoPreview} alt="Preview" className="w-24 h-24 rounded-xl object-cover border-2 border-blue-300 flex-shrink-0" />
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-semibold text-gray-700 truncate">{fotoFile?.name}</p>
                    <p className="text-xs text-gray-400 mt-0.5">{fotoFile ? `${(fotoFile.size / 1024).toFixed(0)} KB` : ''}</p>
                    <p className="text-xs text-emerald-600 mt-1 flex items-center gap-1">
                      <CheckCircle className="w-3.5 h-3.5" /> Foto selecionada
                    </p>
                  </div>
                  <button type="button" onClick={() => { setFotoFile(null); setFotoPreview(null); if (fotoRef.current) fotoRef.current.value = ''; }}
                    className="p-2 rounded-lg bg-red-100 text-red-500 hover:bg-red-200 flex-shrink-0">
                    <X className="w-4 h-4" />
                  </button>
                </div>
              ) : (
                <label className="flex flex-col items-center justify-center gap-3 py-10 border-2 border-dashed border-gray-300 rounded-xl cursor-pointer hover:border-blue-400 hover:bg-blue-50/30 transition-all">
                  <div className="w-14 h-14 rounded-2xl bg-gray-100 flex items-center justify-center">
                    <Camera className="w-7 h-7 text-gray-400" />
                  </div>
                  <div className="text-center">
                    <p className="text-sm font-semibold text-gray-600">Clique para selecionar foto</p>
                    <p className="text-xs text-gray-400 mt-1">JPG, PNG ou WEBP — máx. 5 MB</p>
                  </div>
                  <input ref={fotoRef} type="file" accept="image/*" onChange={handleFoto} className="hidden" />
                </label>
              )}
            </div>
          )}

          {/* ── ETAPA 4: Documentos ───────────────────────────────────────── */}
          {etapa === 4 && (
            <div className="space-y-4">
              {sucesso ? (
                <div className="flex flex-col items-center justify-center py-10 gap-4 text-center">
                  <div className="w-16 h-16 rounded-2xl bg-green-100 flex items-center justify-center">
                    <CheckCircle className="w-8 h-8 text-green-500" />
                  </div>
                  <div>
                    <p className="text-xl font-black text-green-700">Aluno cadastrado com sucesso!</p>
                    <p className="text-sm text-gray-500 mt-1 font-mono">{systemId}</p>
                  </div>
                  <p className="text-xs text-gray-400 max-w-xs">O aluno pode fazer login com o e-mail e senha padrão.</p>
                </div>
              ) : (
                <>
                  <p className="text-sm text-gray-600">Anexe os documentos do aluno. Os documentos ficam arquivados na plataforma. <span className="text-gray-400">(opcionais)</span></p>
                  <div className="space-y-3">
                    <DocCard
                      titulo="Documento de Identidade"
                      descricao="CPF, RG ou CNH"
                      slot={slotDoc1}
                      onSelecionar={f => selecionarDoc(setSlotDoc1, f)}
                      onRemover={() => removerDoc(setSlotDoc1, slotDoc1)}
                    />
                    <DocCard
                      titulo="Comprovante de Endereço"
                      descricao="Conta de luz, água, internet, etc."
                      slot={slotDoc2}
                      onSelecionar={f => selecionarDoc(setSlotDoc2, f)}
                      onRemover={() => removerDoc(setSlotDoc2, slotDoc2)}
                    />
                  </div>
                  {(slotDoc1.erro || slotDoc2.erro) && (
                    <div className="p-3 bg-red-50 border border-red-200 rounded-xl text-xs text-red-600 space-y-0.5">
                      {slotDoc1.erro && <p>Documento 1: {slotDoc1.erro}</p>}
                      {slotDoc2.erro && <p>Documento 2: {slotDoc2.erro}</p>}
                    </div>
                  )}
                </>
              )}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-gray-200 bg-gray-50 flex items-center justify-between flex-shrink-0">
          {/* Botão Anterior */}
          <button
            onClick={() => etapa > 1 ? setEtapa((etapa - 1) as 1|2|3|4) : onFechar()}
            disabled={etapa === 3 || etapa === 4 || salvando}
            className="flex items-center gap-2 px-5 py-2.5 rounded-xl border-2 border-gray-300 text-gray-700 font-bold hover:bg-gray-100 transition-colors disabled:opacity-40 disabled:cursor-not-allowed">
            <ChevronLeft className="w-4 h-4" />
            {etapa === 1 ? 'Cancelar' : 'Anterior'}
          </button>

          {/* Botão Próximo / Ação */}
          {etapa === 1 && (
            <button onClick={() => {
              if (!filiadoSelecionado) { setErro('Selecione uma escola primeiro'); return; }
              setErro(null); setEtapa(2);
            }}
              className="flex items-center gap-2 px-6 py-2.5 rounded-xl bg-blue-600 text-white font-bold hover:bg-blue-700 transition-colors">
              Próxima <ChevronRight className="w-4 h-4" />
            </button>
          )}

          {etapa === 2 && (
            <button onClick={handleCadastrar} disabled={salvando}
              className="flex items-center gap-2 px-6 py-2.5 rounded-xl bg-blue-600 text-white font-bold hover:bg-blue-700 disabled:opacity-60 transition-colors">
              {salvando ? <><Loader2 className="w-4 h-4 animate-spin" /> Cadastrando...</> : <>Cadastrar <ChevronRight className="w-4 h-4" /></>}
            </button>
          )}

          {etapa === 3 && (
            <div className="flex items-center gap-3">
              <button onClick={() => setEtapa(4)} disabled={salvando}
                className="px-4 py-2.5 rounded-xl border-2 border-gray-300 text-gray-600 font-bold text-sm hover:bg-gray-100 disabled:opacity-40 transition-colors">
                Pular foto
              </button>
              <button onClick={handleProximoFoto} disabled={salvando}
                className="flex items-center gap-2 px-6 py-2.5 rounded-xl bg-blue-600 text-white font-bold hover:bg-blue-700 disabled:opacity-60 transition-colors">
                {salvando ? <><Loader2 className="w-4 h-4 animate-spin" /> Enviando...</> : <>{fotoFile ? 'Enviar e Continuar' : 'Próxima'} <ChevronRight className="w-4 h-4" /></>}
              </button>
            </div>
          )}

          {etapa === 4 && !sucesso && (
            <div className="flex items-center gap-3">
              <button onClick={onSucesso} disabled={salvando}
                className="px-4 py-2.5 rounded-xl border-2 border-gray-300 text-gray-600 font-bold text-sm hover:bg-gray-100 disabled:opacity-40 transition-colors">
                Pular documentos
              </button>
              <button onClick={handleFinalizar} disabled={salvando || (!slotDoc1.arquivo && !slotDoc2.arquivo)}
                className="flex items-center gap-2 px-6 py-2.5 rounded-xl bg-green-600 text-white font-bold hover:bg-green-700 disabled:opacity-50 transition-colors">
                {salvando ? <><Loader2 className="w-4 h-4 animate-spin" /> Enviando...</> : <><Upload className="w-4 h-4" /> Enviar e Finalizar</>}
              </button>
            </div>
          )}

          {etapa === 4 && sucesso && (
            <button onClick={onSucesso}
              className="flex items-center gap-2 px-6 py-2.5 rounded-xl bg-green-600 text-white font-bold hover:bg-green-700 transition-colors">
              <UserPlus className="w-4 h-4" /> Concluir
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default ModalCadastroAluno;